import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Paper,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, subDays, startOfMonth, startOfYear } from "date-fns";
import { COMPARISON_MODES, DEFAULT_COMPARISON_MODE } from "./analytics.utils";

const PRESETS = [
  { key: "30days", label: "Last 30 days", start: () => subDays(new Date(), 29) },
  { key: "90days", label: "Last 90 days", start: () => subDays(new Date(), 89) },
  { key: "mtd", label: "Month to date", start: () => startOfMonth(new Date()) },
  { key: "ytd", label: "Year to date", start: () => startOfYear(new Date()) },
  {
    key: "365days",
    label: "Last 12 months",
    start: () => subDays(new Date(), 364),
  },
  { key: "custom", label: "Custom range", start: null },
];

const GRANULARITIES = [
  { key: "", label: "Auto" },
  { key: "day", label: "Daily" },
  { key: "week", label: "Weekly" },
  { key: "month", label: "Monthly" },
];

// The API takes plain YYYY-MM-DD and anchors the range in the business's
// timezone. Sending a full Date would drag the admin's browser timezone into
// the report and shift the boundary days.
const toApiDate = (date) => (date ? format(date, "yyyy-MM-dd") : null);

export default function DateRangeSelector({ onChange }) {
  const [preset, setPreset] = useState("30days");
  const [startDate, setStartDate] = useState(subDays(new Date(), 29));
  const [endDate, setEndDate] = useState(new Date());
  const [granularity, setGranularity] = useState("");
  const [compareTo, setCompareTo] = useState(DEFAULT_COMPARISON_MODE);

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    onChange({
      startDate: toApiDate(startDate),
      endDate: toApiDate(endDate),
      granularity: granularity || null,
      compareTo,
    });
  }, [startDate, endDate, granularity, compareTo, onChange]);

  const handlePresetChange = (event) => {
    const key = event.target.value;
    setPreset(key);
    const match = PRESETS.find((option) => option.key === key);
    if (match && match.start) {
      setStartDate(match.start());
      setEndDate(new Date());
    }
  };

  // Editing either date directly is a custom range by definition — otherwise
  // the preset label keeps claiming "Last 30 days" over a hand-picked window.
  const handleStartChange = (date) => {
    setPreset("custom");
    setStartDate(date);
  };

  const handleEndChange = (date) => {
    setPreset("custom");
    setEndDate(date);
  };

  const invalidRange = Boolean(startDate && endDate && startDate > endDate);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="analytics-preset-label">Range</InputLabel>
              <Select
                labelId="analytics-preset-label"
                label="Range"
                value={preset}
                onChange={handlePresetChange}
              >
                {PRESETS.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={handleStartChange}
              maxDate={endDate || new Date()}
              renderInput={(params) => (
                <TextField {...params} fullWidth size="small" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              label="End date"
              value={endDate}
              onChange={handleEndChange}
              minDate={startDate}
              maxDate={new Date()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  error={invalidRange}
                  helperText={
                    invalidRange ? "End date must follow the start date" : ""
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="analytics-granularity-label">Group by</InputLabel>
              <Select
                labelId="analytics-granularity-label"
                label="Group by"
                value={granularity}
                onChange={(event) => setGranularity(event.target.value)}
              >
                {GRANULARITIES.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="analytics-comparison-label">
                Compare to
              </InputLabel>
              <Select
                labelId="analytics-comparison-label"
                label="Compare to"
                value={compareTo}
                onChange={(event) => setCompareTo(event.target.value)}
              >
                {COMPARISON_MODES.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    </LocalizationProvider>
  );
}
